import { useAuth0 } from "@auth0/auth0-react"
import { useEffect, useReducer } from "react"
import { useParams } from "react-router-dom"
import { isResellerAdmin, isSuperAdmin } from "../../../Utils/util"
import { TransferDeviceToOrgModalReducer } from "../store/reducers"
import { TransferDeviceToOrgModalStates } from "../store/states"

/**
 * This is a custom hook for TransferDeviceToOrgModal which will be used to transfer device to another organization.
 * @param {String} type the type of the modal (device or org)
 * @param {Function} refresh Refresh the data - on the device page, it will refetch the organization data, on the organization page, it will refetch the device data.
 * @param {Object} device The device object which will be transferred to another organization. This object will only be used on the device detail page.
 * @returns {Object} state, toggleModalHandler, handleSeslectItem, confirmMoveDevice, cancelMoveDevice, handleOKButtonClicked
 */
export const useTransferDeviceToOrgModal = (type, refresh, device) => {
	/**
	 * Use Auth0 to get the user object.
	 * @returns {Object} user object - The usage here is to check if the user is a super admin or reseller admin. The user and admin roll can not mount this component.
	 * @returns {Function} getAccessTokenSilently - The usage here is to get the access token silently.
	 */
	const { user, getAccessTokenSilently } = useAuth0()

	/**
	 * Get the id from the URL.
	 * @returns {String} id - The id will be device id or organization id depending on the page where the modal is used. On the device detail page, it will be device id. On the organization detail page, it will be organization id.
	 */
	const { id } = useParams()

	/**
	 * Use the useReducer hook to manage the state of the component.
	 * @returns {Object} state - The state object contains all the states of the component.
	 * @returns {Function} dispatch - The dispatch function is used to dispatch the action to the reducer.
	 */
	const [state, dispatch] = useReducer(TransferDeviceToOrgModalReducer, TransferDeviceToOrgModalStates)

	/**
	 * This function will handle the static UI element of the modal. Which will set the static strings when the component is mounted depending on the type of the modal.
	 */
	const handleStaticUIElement = () => {
		switch (type) {
			case "org":
				dispatch({
					type: "SET_STATIC_UI_ELEMENT",
					payload: {
						...state.staticUIElement,
						title: "Add Device to Organization",
						placeholder: "Please select a device",
						noResultsMessage: "No device found"
					}
				})
				break
			case "device":
				dispatch({
					type: "SET_STATIC_UI_ELEMENT",
					payload: {
						...state.staticUIElement,
						title: "Move Device to Organization",
						placeholder: "Please select an organization",
						noResultsMessage: "No organization found"
					}
				})
				break
			default:
				console.log("Invalid type")
				break
		}
	}

	/**
	 * This function will toggle the modal open and close.
	 */
	const toggleModalHandler = () => {
		dispatch({ type: "TOGGLE_TRANSFER_DEVICE_TO_ORG_MODAL" })
	}

	/**
	 * This function will handle the selected item from the dropdown. If the type is "device", it will set the selected organization. If the type is "org", it will set the selected device.
	 * @param {Event} e - the dropdown event object. Here just as the parameter placeholder. Not used in the function.
	 * @param {Object} data - the selected item object. It contains the key, text, and value of the selected item.
	 */
	const handleSeslectItem = (e, data) => {
		switch (type) {
			case "device":
				dispatch({ type: "SET_SELECTED_ORG", payload: data.value })
				break
			case "org":
				dispatch({ type: "SET_SELECTED_DEVICE", payload: data.value })
				break
			default:
				break
		}
	}

	/**
	 * This function will handle the OK button clicked. If the move is successful, it will refresh the data.
	 * Then reset the state of the component and close the modal.
	 */
	const handleOKButtonClicked = () => {
		if (state.isSuccess) refresh()
		dispatch({ type: "CANCEL_MOVE_DEVICE" })
	}

	/**
	 * This function will confirm the move of the device to the organization. If the selected item is not selected, it will set the error and warning the user.
	 * If the selected item is selected, it will patch the device to the organization.
	 * Depending on the type of the modal, the patchD2O function will have different parameters.
	 * If the type is "device", it will patch the selected device to the organization.
	 * If the type is "org", it will patch the device to the selected organization.
	 */
	const confirmMoveDevice = () => {
		switch (type) {
			case "org":
				if (state.selectedDevice === null) {
					dispatch({ type: "SET_NOT_SELECTED" })
					return
				}
				patchD2O(state.selectedDevice, id)
				break
			case "device":
				if (state.selectedOrg === null) {
					dispatch({ type: "SET_NOT_SELECTED" })
					return
				}
				patchD2O(id, state.selectedOrg)
				break
			default:
				break
		}
	}

	/**
	 * This function will cancel the move of the device to the organization. It will reset the state of the component.
	 */
	const cancelMoveDevice = () => {
		dispatch({ type: "CANCEL_MOVE_DEVICE" })
	}

	/**
	 * This function will transform the organization data to the dropdown list.
	 * @param {Array} data - the organization data from the server.
	 */
	const transformOrgData = (data) =>
		data.map((org) => ({
			key: org.id,
			text: org.name,
			value: org.id
		}))

	/**
	 * This function will transform the devices data based on the user role.
	 * If the user is a super admin, it will return all the devices except the device which is in the same organization as the user.
	 * If the user is a reseller admin, it will return all the devices which are in the same organization as the user.
	 * @param {Array} data - the device data from the server.
	 */
	const transformDeviceData = (data) => {
		if (isSuperAdmin(user)) {
			return data
				.filter((item) => item.org_id !== +id)
				.map((item) => ({
					key: item.id,
					text: item.shortname,
					value: item.id
				}))
		}
		if (isResellerAdmin(user)) {
			return data
				.filter((item) => item.org_id === user.orgId)
				.map((item) => ({
					key: item.id,
					text: item.shortname,
					value: item.id
				}))
		}
	}

	/**
	 * This function will fetch the organization data from the server.
	 * This function will be called when the type is "device." Then, update the org data used for the dropdown list.
	 * @returns {Promise} fetchOrgData
	 */
	const fetchOrgData = async () => {
		dispatch({ type: "SET_LOADING_ORG", payload: true })
		return fetch(
			new Request(process.env.REACT_APP_SESSION_ADDR + "/api/v2/org", {
				method: "GET",
				headers: new Headers({
					"authorization": "Bearer " + (await getAccessTokenSilently()),
					"Content-Type": "application/json"
				})
			})
		)
			.then((response) => {
				if (!response.ok) {
					throw new Error("Cannot fetch org data")
				}
				return response.json()
			})
			.then((data) => transformOrgData(data))
			.then((data) => dispatch({ type: "SET_DATA", payload: data }))
			.catch((error) => dispatch({ type: "SET_ERROR", payload: error.message }))
			.finally(() => dispatch({ type: "SET_LOADING_ORG", payload: false }))
	}

	/**
	 * This function will fetch the device data from the server.
	 * This function will be called when the type is "org." Then, update the device data used for the dropdown list.
	 * @returns {Promise} fetchDeviceData
	 */
	const fetchDeviceData = async () => {
		dispatch({ type: "SET_LOADING_ORG", payload: true })
		return fetch(
			new Request(process.env.REACT_APP_SESSION_ADDR + "/api/v2/device/getTable", {
				method: "GET",
				headers: new Headers({
					"authorization": "Bearer " + (await getAccessTokenSilently()),
					"Content-Type": "application/json"
				})
			})
		)
			.then((response) => {
				if (!response.ok) {
					throw new Error("Cannot fetch device data")
				}
				return response.json()
			})
			.then((data) => transformDeviceData(data))
			.then((data) => dispatch({ type: "SET_DATA", payload: data }))
			.catch((error) => dispatch({ type: "SET_ERROR", payload: error.message }))
			.finally(() => dispatch({ type: "SET_LOADING_ORG", payload: false }))
	}

	/**
	 * This function will patch the device to the organization
	 * @param {Number} deviceID - The device ID which will be patched to the organization.
	 * @param {Number} orgID - The organization ID which is the target organization.
	 * @returns
	 */
	const patchD2O = async (deviceID, orgID) => {
		dispatch({ type: "SET_LOADING", payload: true })
		return fetch(
			new Request(process.env.REACT_APP_SESSION_ADDR + "/api/v2/moveD2O/" + deviceID + "/" + orgID, {
				method: "PATCH",
				headers: new Headers({
					"authorization": "Bearer " + (await getAccessTokenSilently()),
					"Content-Type": "application/json"
				})
			})
		)
			.then((response) => {
				if (response.ok) {
					dispatch({ type: "SUCCESS_MOVE_DEVICE" })
				} else if (response.status === 401) {
					throw new Error("Cannot move the device to the organization")
				} else {
					throw new Error("Something went wrong")
				}
			})
			.catch((error) => dispatch({ type: "SET_ERROR", payload: error.message }))
			.finally(() => dispatch({ type: "SET_LOADING", payload: false }))
	}

	/**
	 * This function will be called when the component is mounted.
	 * It will set up the static UI element and fetch the data based on the type of the modal.
	 */
	useEffect(() => {
		handleStaticUIElement()
		if (type === "device") fetchOrgData()
		if (type === "org") fetchDeviceData()
	}, [type])

	/**
	 * This function will check the user role to disable the button of the modal.
	 * In the device detail page, if the user is a reseller admin and the device is not in the same organization as the user, it will disable the button.
	 * In the org detail page, if the user is a reseller admin and the user is in the same organization as the device, it will disable the button.
	 */
	useEffect(() => {
		switch (type) {
			case "device":
				if (isResellerAdmin(user) && user.orgId !== device.org_id) {
					dispatch({ type: "SET_DISABLED" })
				}
				break
			case "org":
				if (isResellerAdmin(user) && user.orgId === +id) {
					dispatch({ type: "SET_DISABLED" })
				}
				break
			default:
				break
		}
	}, [user, device])

	return { state, toggleModalHandler, handleSeslectItem, confirmMoveDevice, cancelMoveDevice, handleOKButtonClicked }
}
